import { WORKER_IDS } from 'app/constants/workers';
import { errorToast } from 'app/utils/toast';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { updataAllUploadsDataById } from 'store/allUploads/actions';

import UploadDataWorker from '../../workers/uploadData.worker.js?worker';
import useFloatingWidgetStates from './FloatingWidgetHooks/FloatingWidget/useFloatingWidgetStates';
import useFolderUploadStates from './FloatingWidgetHooks/FolderUpload/useFolderUploadStates';
import useWebWorker from './useWebWorker';
// import {
//   resetStatusWidget,
//   setFolderError,
//   setFolderUploadComplete
// } from 'store/statusWidget/actions';
// import useFloatingStatusWidgetState from './useFloatingStatusWidgetState';

let currentProps;

const useUploadDataWorker = props => {
  const worker = useWebWorker(
    WORKER_IDS.UPLOAD_DATA,
    () => new UploadDataWorker()
  );

  const dispatch = useDispatch();

  const [, customDispatch, { setFolderUploadComplete, setFolderError }] =
    useFolderUploadStates();
  const [, , { setStatusWidget }] = useFloatingWidgetStates(true);

  const sessionMeta = useRef(null);

  const onAddFilesToState = useCallback((session, files) => {
    worker.postMessage({
      type: 'ADD_FILES_TO_STATE',
      payload: { session, files },
    });
  }, []);

  const onFileUploadStart = useCallback(session => {
    if (props) {
      currentProps = props;
    }
    sessionMeta.current = session;

    worker.postMessage({
      type: 'START_FILES_UPLOAD',
      payload: { sessionId: session.upload_task_id },
    });
  }, []);

  const onFileRetry = useCallback(session => {
    sessionMeta.current = session;

    worker.postMessage({
      type: 'RETRY_FILE_UPLOAD',
      payload: { sessionId: session.upload_task_id },
    });
  }, []);

  const setProgress = useCallback(({ sessionId, percentage }) => {
    if (
      currentProps?.onProgress &&
      sessionMeta.current?.upload_task_id === sessionId
    ) {
      currentProps.onProgress(percentage);
    }
  }, []);

  const onUploadComplete = useCallback(({ session }) => {
    session = JSON.parse(session);

    customDispatch(setFolderUploadComplete(session.upload_task_id));

    const payload = {
      ...session,
      use_case_name: currentProps.useCase.name,
      use_case_type: currentProps.useCase.type,
      file_sets_count: session.file_sets_count,
      unlabelled_file_sets_count: session.unlabelled_file_sets_count,
      unreviewed_file_sets_count: session.unreviewed_file_sets_count,
    };

    dispatch(updataAllUploadsDataById(payload));

    if (sessionMeta) {
      sessionMeta.current = null;
    }
  }, []);

  const onUploadFail = useCallback(({ session, failed }) => {
    session = JSON.parse(session);

    customDispatch(setFolderError(session.upload_task_id));

    const payload = {
      ...session,
      use_case_name: currentProps.useCase.name,
      use_case_type: currentProps.useCase.type,
      file_sets_count: session.file_sets_count,
      unlabelled_file_sets_count: session.unlabelled_file_sets_count,
      unreviewed_file_sets_count: session.unreviewed_file_sets_count,
    };

    dispatch(updataAllUploadsDataById(payload));

    errorToast(`${failed} files failed to upload.`);

    if (sessionMeta) {
      sessionMeta.current = null;
    }
  }, []);

  const onTerminate = useCallback(() => {
    worker.terminate();
    worker.invoke();
    customDispatch(setStatusWidget(false));
  }, []);

  useEffect(() => {
    worker.onmessage = ({ data }) => {
      switch (data.type) {
        case 'SET_PROGRESS':
          setProgress(data.payload);
          break;

        case 'UPLOAD_COMPLETE':
          onUploadComplete(data.payload);
          break;

        case 'FAILED_UPLOADS':
          onUploadFail(data.payload);
          break;

        default:
          throw new Error('Invalid request type');
      }
    };
  }, []);

  return {
    onAddFilesToState,
    onFileUploadStart,
    onFileRetry,
    onTerminate,
  };
};
export default useUploadDataWorker;
